import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '../utils/api';
import useSSE from '../useSSE';

const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const STATUS_COLOR = {
  not_started:'#8A93A6', planning:'#2563EB', in_progress:'#5B5BD6',
  at_risk:'#D97706', on_hold:'#8A93A6', escalated:'#DC2626',
  completed:'#16A34A', cancelled:'#94A3B8',
};

function fmtTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T09:00:00');
  return d.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' });
}

export default function CalendarPage() {
  const { spaceId } = useParams();
  const navigate     = useNavigate();
  const ctx          = useOutletContext();
  const backToDash   = () => navigate(spaceId ? `/spaces/${spaceId}` : '/spaces');

  const today        = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [meetings, setMeetings] = useState([]);
  const [selected, setSelected] = useState(null); // Date string YYYY-MM-DD
  const [detail, setDetail]     = useState(null); // meeting detail modal
  const [loading, setLoading]   = useState(true);

  // Load all meeting-type tasks for this project
  const load = useCallback(async () => {
    try {
      const data = await api.get(`/spaces/${spaceId}/meetings`).catch(() => []);
      setMeetings(Array.isArray(data) ? data : []);
    } catch(_) {}
    setLoading(false);
  }, [spaceId]);

  useEffect(() => { load(); }, [load]);
  useSSE(spaceId, { task_updated: () => load() });

  // Build calendar grid
  const firstDay  = new Date(year, month, 1).getDay();
  const daysInMon = new Date(year, month + 1, 0).getDate();
  const cells     = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMon; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  function dateKey(d) {
    return `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  }

  function meetingsOnDay(d) {
    if (!d) return [];
    const key = dateKey(d);
    return meetings.filter(m => {
      const due = m.due_date || m.start_date;
      return due && due.startsWith(key);
    });

  // Silent reload when phase access changes (permission granted/revoked)
  React.useEffect(() => {
    function onAccessChanged() {
      silentRef.current = true;
      load().finally(() => { silentRef.current = false; });
    }
    window.addEventListener('trajectory:access_changed', onAccessChanged);
    return () => window.removeEventListener('trajectory:access_changed', onAccessChanged);
  }, [spaceId]);
  }

  const isToday = (d) => d &&
    today.getDate()===d && today.getMonth()===month && today.getFullYear()===year;

  function prevMonth() { month===0 ? (setYear(y=>y-1),setMonth(11)) : setMonth(m=>m-1); }
  function nextMonth() { month===11 ? (setYear(y=>y+1),setMonth(0)) : setMonth(m=>m+1); }

  const selectedMeetings = selected
    ? meetings.filter(m => (m.due_date||m.start_date||'').startsWith(selected))
    : [];

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',background:'var(--bg)',minHeight:0,position:'relative'}}>

      {/* Back pill */}
      <button onClick={backToDash}
        style={{position:'fixed',top:10,left:200,zIndex:200,
          padding:'4px 12px',background:'white',border:'1px solid #E2E8F0',
          borderRadius:'16px',cursor:'pointer',fontSize:'11px',fontWeight:'600',
          color:'#64748B',boxShadow:'0 1px 3px rgba(0,0,0,.1)',
          display:'flex',alignItems:'center',gap:'4px',transition:'all .15s'}}
        onMouseEnter={e=>{e.currentTarget.style.background='#5B5BD6';e.currentTarget.style.color='white';e.currentTarget.style.borderColor='#5B5BD6';}}
        onMouseLeave={e=>{e.currentTarget.style.background='white';e.currentTarget.style.color='#64748B';e.currentTarget.style.borderColor='#E2E8F0';}}>
        ← Overview
      </button>

      <div style={{padding:'24px 28px',flex:1,overflowY:'auto'}}>

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'24px',marginTop:'8px'}}>
          <div>
            <h2 style={{margin:0,fontSize:'22px',fontWeight:'800',color:'var(--text)'}}>
              📅 Project Calendar
            </h2>
            <div style={{fontSize:'12px',color:'var(--muted)',marginTop:'3px'}}>
              Meetings and scheduled events for this project
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
            <button onClick={prevMonth} className="btn btn-sm" style={{padding:'6px 12px',fontWeight:'700'}}>‹</button>
            <span style={{fontSize:'16px',fontWeight:'800',color:'var(--text)',minWidth:'160px',textAlign:'center'}}>
              {MONTHS[month]} {year}
            </span>
            <button onClick={nextMonth} className="btn btn-sm" style={{padding:'6px 12px',fontWeight:'700'}}>›</button>
            <button onClick={()=>{setYear(today.getFullYear());setMonth(today.getMonth());setSelected(null);}}
              className="btn btn-sm btn-primary" style={{marginLeft:'8px'}}>Today</button>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:'20px',alignItems:'start'}}>

          {/* Calendar grid */}
          <div className="card" style={{padding:'0',overflow:'hidden'}}>
            {/* Day headers */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',background:'var(--surface2)',borderBottom:'1px solid var(--border)'}}>
              {DAYS.map(d=>(
                <div key={d} style={{padding:'10px 0',textAlign:'center',fontSize:'11px',fontWeight:'700',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.5px'}}>
                  {d}
                </div>
              ))}
            </div>
            {/* Weeks */}
            {Array.from({length: cells.length/7}, (_,wi) => (
              <div key={wi} style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',borderBottom:'1px solid var(--border)'}}>
                {cells.slice(wi*7, wi*7+7).map((d,di) => {
                  const dayMeetings = meetingsOnDay(d);
                  const key = d ? dateKey(d) : null;
                  const isSelected = key && key===selected;
                  const isTodayD = isToday(d);
                  return (
                    <div key={di}
                      onClick={()=>d&&setSelected(isSelected?null:key)}
                      style={{
                        minHeight:`${Math.max(80, dayMeetings.length * 22 + 36)}px`,padding:'6px',
                        borderRight: di<6 ? '1px solid var(--border)' : 'none',
                        background: isSelected?'rgba(91,91,214,0.06)':isTodayD?'rgba(91,91,214,0.03)':'transparent',
                        cursor: d ? 'pointer' : 'default',
                        transition:'background .1s',
                      }}>
                      {d && (
                        <>
                          <div style={{
                            width:'26px',height:'26px',borderRadius:'50%',
                            background: isTodayD?'#5B5BD6':'transparent',
                            color: isTodayD?'white':'var(--text)',
                            display:'flex',alignItems:'center',justifyContent:'center',
                            fontSize:'13px',fontWeight: isTodayD?'800':'600',
                            marginBottom:'4px',
                          }}>{d}</div>
                          {dayMeetings.slice(0,3).map(m=>(
                            <div key={m.id}
                              onClick={e=>{e.stopPropagation();setDetail(m);}}
                              style={{
                                fontSize:'10px',fontWeight:'600',
                                background: (STATUS_COLOR[m.status]||'#5B5BD6')+'22',
                                color: STATUS_COLOR[m.status]||'#5B5BD6',
                                borderLeft:`3px solid ${STATUS_COLOR[m.status]||'#5B5BD6'}`,
                                borderRadius:'0 3px 3px 0',
                                padding:'2px 5px',marginBottom:'2px',
                                overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis',
                                cursor:'pointer',
                              }}
                              title={m.name}>
                              📍 {m.name}
                            </div>
                          ))}
                          {dayMeetings.length>3 && (
                            <div style={{fontSize:'9px',color:'var(--muted)',paddingLeft:'4px'}}>
                              +{dayMeetings.length-3} more
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Side panel — selected day or upcoming */}
          <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
            {/* Selected day meetings */}
            {selected ? (
              <div className="card" style={{padding:'16px'}}>
                <div style={{fontSize:'13px',fontWeight:'700',color:'var(--text)',marginBottom:'12px'}}>
                  📅 {new Date(selected+'T12:00:00').toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long'})}
                </div>
                {selectedMeetings.length===0 ? (
                  <div style={{fontSize:'12px',color:'var(--muted)',textAlign:'center',padding:'20px 0'}}>
                    No meetings scheduled
                  </div>
                ) : selectedMeetings.map(m=>(
                  <div key={m.id} onClick={()=>setDetail(m)}
                    style={{padding:'10px 12px',borderRadius:'8px',marginBottom:'8px',
                      border:`1px solid ${STATUS_COLOR[m.status]||'#5B5BD6'}33`,
                      background:`${STATUS_COLOR[m.status]||'#5B5BD6'}08`,
                      cursor:'pointer',transition:'all .15s'}}
                    onMouseEnter={e=>e.currentTarget.style.background=`${STATUS_COLOR[m.status]||'#5B5BD6'}16`}
                    onMouseLeave={e=>e.currentTarget.style.background=`${STATUS_COLOR[m.status]||'#5B5BD6'}08`}>
                    <div style={{fontSize:'12px',fontWeight:'700',color:'var(--text)',marginBottom:'3px'}}>
                      📍 {m.name}
                    </div>
                    <div style={{fontSize:'10px',color:'var(--muted)',display:'flex',gap:'8px',flexWrap:'wrap'}}>
                      <span>{m.phase_name||'—'} › {m.list_name||'—'}</span>
                      <span style={{color:STATUS_COLOR[m.status]||'#5B5BD6',fontWeight:'600',textTransform:'capitalize'}}>
                        {(m.status||'').replace(/_/g,' ')}
                      </span>
                    </div>
                    {m.assignees?.length>0 && (
                      <div style={{fontSize:'10px',color:'var(--muted)',marginTop:'4px'}}>
                        👤 {m.assignees.map(a=>a.full_name).join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* Upcoming meetings */
              <div className="card" style={{padding:'16px'}}>
                <div style={{fontSize:'13px',fontWeight:'700',color:'var(--text)',marginBottom:'12px'}}>
                  🗓 Upcoming Meetings
                </div>
                {loading ? (
                  <div style={{color:'var(--muted)',fontSize:'12px',textAlign:'center',padding:'16px'}}>Loading…</div>
                ) : meetings.filter(m=>{
                    const d=m.due_date||m.start_date;
                    return d && d>=new Date().toISOString().slice(0,10);
                  }).slice(0,8).map(m=>(
                  <div key={m.id} onClick={()=>setDetail(m)}
                    style={{padding:'8px 10px',borderRadius:'7px',marginBottom:'6px',
                      border:`1px solid ${STATUS_COLOR[m.status]||'#5B5BD6'}22`,
                      background:'var(--surface2)',cursor:'pointer',transition:'all .15s'}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--surface3)'}
                    onMouseLeave={e=>e.currentTarget.style.background='var(--surface2)'}>
                    <div style={{fontSize:'11px',fontWeight:'700',color:'var(--text)'}}>📍 {m.name}</div>
                    <div style={{fontSize:'10px',color:'var(--muted)',marginTop:'2px'}}>
                      {m.due_date||m.start_date} · {m.list_name||'—'}
                    </div>
                  </div>
                ))}
                {meetings.filter(m=>{const d=m.due_date||m.start_date;return d&&d>=new Date().toISOString().slice(0,10);}).length===0&&(
                  <div style={{fontSize:'12px',color:'var(--muted)',textAlign:'center',padding:'16px 0'}}>No upcoming meetings</div>
                )}
              </div>
            )}

            {/* Summary */}
            <div className="card" style={{padding:'14px'}}>
              <div style={{fontSize:'11px',fontWeight:'700',color:'var(--text)',marginBottom:'10px',textTransform:'uppercase',letterSpacing:'.5px'}}>
                Meeting Summary
              </div>
              {[
                ['Total','📋',meetings.length,'#5B5BD6'],
                ['Completed','✅',meetings.filter(m=>m.status==='completed').length,'#16A34A'],
                ['Upcoming','📅',meetings.filter(m=>{const d=m.due_date||m.start_date;return d&&d>=new Date().toISOString().slice(0,10)&&m.status!=='completed';}).length,'#D97706'],
                ['At Risk','⚠️',meetings.filter(m=>m.status==='at_risk').length,'#DC2626'],
              ].map(([label,icon,count,color])=>(
                <div key={label} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid var(--border)'}}>
                  <span style={{fontSize:'12px',color:'var(--muted)'}}>{icon} {label}</span>
                  <span style={{fontSize:'14px',fontWeight:'800',color}}>{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Meeting detail modal */}
      {detail && (
        <div onClick={()=>setDetail(null)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:'var(--surface)',borderRadius:'14px',padding:'24px',width:'480px',maxWidth:'95vw',boxShadow:'0 20px 60px rgba(0,0,0,.25)',maxHeight:'80vh',overflowY:'auto'}}>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'16px'}}>
              <div>
                <div style={{fontSize:'16px',fontWeight:'800',color:'var(--text)',marginBottom:'4px'}}>📍 {detail.name}</div>
                <div style={{fontSize:'11px',color:'var(--muted)'}}>
                  {detail.phase_name||'—'} › {detail.list_name||'—'}
                </div>
              </div>
              <button onClick={()=>setDetail(null)}
                style={{background:'none',border:'none',fontSize:'18px',cursor:'pointer',color:'var(--muted)',padding:'2px 6px'}}>×</button>
            </div>
            {[
              ['Status', (detail.status||'').replace(/_/g,' '), STATUS_COLOR[detail.status]||'#8A93A6'],
              ['Priority', detail.priority||'normal', '#D97706'],
              ['Start Date', detail.start_date||'—', 'var(--text)'],
              ['Due Date', detail.due_date||'—', detail.due_date<new Date().toISOString().slice(0,10)?'#DC2626':'var(--text)'],
              ['Progress', `${Math.round(detail.progress||0)}%`, '#5B5BD6'],
            ].map(([label, val, color])=>(
              <div key={label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
                <span style={{fontSize:'12px',color:'var(--muted)',fontWeight:'600'}}>{label}</span>
                <span style={{fontSize:'13px',fontWeight:'700',color,textTransform:'capitalize'}}>{val}</span>
              </div>
            ))}
            {detail.description && (
              <div style={{marginTop:'12px',fontSize:'12px',color:'var(--muted)',lineHeight:'1.6',background:'var(--surface2)',borderRadius:'8px',padding:'10px 12px'}}>
                {detail.description}
              </div>
            )}
            {detail.assignees?.length>0 && (
              <div style={{marginTop:'12px'}}>
                <div style={{fontSize:'11px',color:'var(--muted)',fontWeight:'700',marginBottom:'6px',textTransform:'uppercase',letterSpacing:'.5px'}}>Attendees</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px'}}>
                  {detail.assignees.map(a=>(
                    <div key={a.id} style={{padding:'3px 10px',background:a.avatar_color+'22',color:a.avatar_color,borderRadius:'99px',fontSize:'11px',fontWeight:'600'}}>
                      {a.full_name}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button onClick={()=>{navigate(`/lists/${detail.milestone_id}`);setDetail(null);}}
              className="btn btn-primary" style={{width:'100%',marginTop:'16px',fontSize:'12px'}}>
              Open in List
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
